'use client';

import Button from '../../button';
import HorizontalLine from '../../horizontal-line';
import { IconCollapse } from '../../icon';
import { useTranslations } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';
import { PropsWithChildren, useState, useEffect } from 'react';
import CloseAlt from '../../icon/close-alt';
import { handlePriceFormat } from './price-functions';
import FocusLock from 'react-focus-lock';

const MobileExpandableComponent = ({
	children,
	isExpanded,
	onToggle,
	amount,
	currencyCode,
}: {
	children: React.ReactNode;
	isExpanded: boolean;
	onToggle: () => void;
	amount?: number;
	currencyCode?: string;
}) => {
	const t = useTranslations();

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && isExpanded) {
				onToggle();
			}
		};

		if (isExpanded) {
			document.addEventListener('keydown', handleKeyDown);
			return () => {
				document.removeEventListener('keydown', handleKeyDown);
			};
		}

		return;
	}, [isExpanded, onToggle]);

	return (
		<div
			className={cn(
				'fixed bottom-0 left-0 right-0 z-[99] border-t h-screen transition-transform duration-500 ease-out border-t-border-subtle bg-surface-raised-subtle',
				{
					'translate-y-0': isExpanded,
					'translate-y-[calc(100%-87px)]': !isExpanded,
					'md:translate-y-[calc(100%-100px)]': !isExpanded,
					'shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.2)]': !isExpanded,
					'rounded-t-[20px]': !isExpanded,
				}
			)}
		>
			{!isExpanded && (
				<div className={cn('w-full flex flex-col spacing-stack-s py-[15px] h-[87px] md:h-[100px]')}>
					<div className="flex flex-row items-center justify-center w-full">
						<Button
							onClick={onToggle}
							intent="unstyled"
							className="flex flex-row spacing-inline-s items-center justify-center"
						>
							<span className="text-label">{t('View your order')}</span>
							<div className="rotate-[180deg]">
								<IconCollapse fill="black" />
							</div>
						</Button>
					</div>
					<HorizontalLine additionalClasses="border-border-subtle" />
					<div className="flex flex-row w-full items-between justify-between px-[30px] text-text-primary">
						<p className="text-subtitle2">{t('Est. Total')}</p>
						<p className="text-subtitle1">{handlePriceFormat({ amount, currencyCode })}</p>
					</div>
				</div>
			)}

			{isExpanded ? (
				<div className="h-full w-full overflow-auto">
					<FocusLock returnFocus>
						<div className="flex flex-row justify-end w-full pt-[30px] pb-[20px] px-[20px] ">
							<Button intent="unstyled" onClick={onToggle}>
								<CloseAlt width={24} height={24} />
							</Button>
						</div>
						{children}
					</FocusLock>
				</div>
			) : null}
		</div>
	);
};

export const MobileExpandableContent = ({
	children,
	amount,
	currencyCode,
}: PropsWithChildren<{ amount?: number; currencyCode?: string }>) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const handleToggle = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<MobileExpandableComponent
			amount={amount}
			currencyCode={currencyCode}
			isExpanded={isExpanded}
			onToggle={handleToggle}
		>
			{children}
		</MobileExpandableComponent>
	);
};
